import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { Plus, Trash2, X, BookOpen } from "lucide-react";

import { API_URL as API } from "../../config";

export default function AcademicsPage() {
  const { authFetch } = useAuth();
  const [athletes, setAthletes] = useState([]);
  const [records, setRecords] = useState([]);
  const [selectedAthlete, setSelectedAthlete] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ athlete_id: "", subject: "", grade_value: "", gpa: "", semester: "", notes: "" });

  useEffect(() => {
    const loadAthletes = async () => {
      const res = await authFetch(`${API}/api/admin/athletes`);
      if (res.ok) setAthletes(await res.json());
    };
    loadAthletes();
  }, [authFetch]);

  useEffect(() => {
    const loadRecords = async () => {
      const url = selectedAthlete ? `${API}/api/admin/academics?athlete_id=${selectedAthlete}` : `${API}/api/admin/academics`;
      const res = await authFetch(url);
      if (res.ok) setRecords(await res.json());
    };
    loadRecords();
  }, [selectedAthlete, authFetch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const body = { ...form, gpa: form.gpa ? parseFloat(form.gpa) : null };
    Object.keys(body).forEach(k => { if (body[k] === "" || body[k] === null) delete body[k]; });
    if (!body.athlete_id || !body.subject) return;
    await authFetch(`${API}/api/admin/academics`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    setShowForm(false);
    setForm({ athlete_id: selectedAthlete || "", subject: "", grade_value: "", gpa: "", semester: "", notes: "" });
    const res = await authFetch(selectedAthlete ? `${API}/api/admin/academics?athlete_id=${selectedAthlete}` : `${API}/api/admin/academics`);
    if (res.ok) setRecords(await res.json());
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    await authFetch(`${API}/api/admin/academics/${id}`, { method: "DELETE" });
    setRecords(records.filter(r => r.id !== id));
  };

  const athleteMap = {};
  athletes.forEach(a => { athleteMap[a.id] = a.name; });

  return (
    <div data-testid="academics-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 font-['Kanit']">Academics</h2>
          <p className="text-gray-500 text-sm">Track academic progress for athletes</p>
        </div>
        <button onClick={() => { setForm({ athlete_id: selectedAthlete || "", subject: "", grade_value: "", gpa: "", semester: "", notes: "" }); setShowForm(true); }} data-testid="add-academic-btn"
          className="flex items-center gap-2 bg-[#87CEEB] hover:bg-[#6bb8d9] text-black px-5 py-2.5 rounded-xl font-semibold text-sm transition-all">
          <Plus className="w-4 h-4" /> {a.addRecord}
        </button>
      </div>

      <div className="mb-4">
        <select value={selectedAthlete} onChange={e => setSelectedAthlete(e.target.value)} data-testid="filter-athlete"
          className="px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]">
          <option value="">{a.allAthletes}</option>
          {athletes.map(at => <option key={at.id} value={at.id}>{at.name}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 font-semibold text-gray-600">{a.athlete}</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">{a.subject}</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden sm:table-cell">{a.grade}</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden sm:table-cell">{a.gpa}</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden md:table-cell">{a.semester}</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-600">{a.actions}</th>
              </tr>
            </thead>
            <tbody>
              {records.length === 0 ? (
                <tr><td colSpan="6" className="px-4 py-12 text-center text-gray-400">
                  <BookOpen className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  No academic records yet.
                </td></tr>
              ) : records.map(r => (
                <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`academic-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium text-gray-900">{athleteMap[r.athlete_id] || r.athlete_id}</td>
                  <td className="px-4 py-3 text-gray-600">{r.subject}</td>
                  <td className="px-4 py-3 text-gray-600 hidden sm:table-cell">{r.grade_value || "-"}</td>
                  <td className="px-4 py-3 text-gray-600 hidden sm:table-cell">{r.gpa != null ? r.gpa.toFixed(1) : "-"}</td>
                  <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{r.semester || "-"}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => handleDelete(r.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-red-500" data-testid={`delete-academic-${r.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md" data-testid="academic-form-modal">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="text-lg font-bold font-['Kanit']">{a.addAcademicRecord}</h3>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{a.athlete} *</label>
                <select value={form.athlete_id} onChange={e => setForm({...form, athlete_id: e.target.value})} required
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" data-testid="academic-athlete-select">
                  <option value="">{a.selectAthlete}</option>
                  {athletes.map(at => <option key={at.id} value={at.id}>{at.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{a.subject} *</label>
                <input value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} required
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" data-testid="academic-subject-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{a.grade}</label>
                  <input value={form.grade_value} onChange={e => setForm({...form, grade_value: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" placeholder="A, B+, etc." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{a.gpa}</label>
                  <input type="number" step="0.1" min="0" max="4" value={form.gpa} onChange={e => setForm({...form, gpa: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" placeholder="0.0 - 4.0" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{a.semester}</label>
                <input value={form.semester} onChange={e => setForm({...form, semester: e.target.value})}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" placeholder="e.g. Fall 2026" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{a.notes}</label>
                <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB] resize-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)}
                  className="flex-1 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50">{a.cancel}</button>
                <button type="submit" data-testid="academic-form-submit"
                  className="flex-1 py-2.5 bg-[#87CEEB] hover:bg-[#6bb8d9] text-black rounded-xl text-sm font-bold">{a.addRecord}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
